import { fetchProducts } from "./catalogueApi";
import { fetchBusinessSettings } from "./businessApi";

// The POS stores no finance collections. What exists is derivable:
//
//   suppliers -> each product carries a prodSupplier block
//   levies    -> settings.financial.levies
//
// Expenses, recurring expenses and discounts have no representation anywhere in
// Firestore, so they return empty rather than mock data. Showing invented
// figures next to real revenue would be worse than showing nothing.

const str = (v) => (typeof v === "string" ? v.trim() : "");

/** One row per distinct supplier, with the products they supply rolled up. */
export async function fetchSuppliers(businessId) {
  if (!businessId) return [];
  const products = await fetchProducts(businessId);

  const byKey = new Map();
  products.forEach((p) => {
    const s = p.supplier || {};
    const name = str(s.name);
    // Products with no supplier filled in contribute nothing.
    if (!name && !str(s.id)) return;

    const key = str(s.id) || name.toLowerCase();
    const entry = byKey.get(key) || {
      id: key,
      name: name || "Unnamed supplier",
      email: s.email,
      phone: s.phone,
      website: s.website,
      leadTime: s.leadTime,
      minimumOrderQuantity: s.minimumOrderQuantity,
      preferred: s.preferred,
      productCount: 0,
      products: [],
      stockValue: 0,
    };

    entry.productCount += 1;
    entry.products.push({ id: p.productId, name: p.productName });
    entry.stockValue += p.inventoryQuantity * p.productCostPrice;
    byKey.set(key, entry);
  });

  return [...byKey.values()].sort((a, b) => b.productCount - a.productCount);
}

/** Levies are configuration on the settings document, not a collection. */
export async function fetchLevies(businessId) {
  if (!businessId) return [];
  const settings = await fetchBusinessSettings(businessId);
  const levies = settings?.levies || [];

  return levies.map((l, index) => ({
    id: str(l.id) || String(index),
    name: str(l.name) || str(l.desc) || `Levy ${index + 1}`,
    description: str(l.desc),
    type: str(l.type) || "Percentage",
    amount: Number(l.amountPer) || Number(l.amount) || 0,
    factoring: str(l.factoring),
    collect: Boolean(l.collect),
  }));
}

// Not tracked by the POS. Kept as functions so the hooks and tables keep their
// shape, and so wiring them up later is a one-line change here.
export async function fetchExpenses() {
  return [];
}

export async function fetchRecurringExpenses() {
  return [];
}

export async function fetchDiscounts() {
  return [];
}
